import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { Paper, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import DataGrid, {
  Column, Editing, Lookup, Paging, SearchPanel,
  Toolbar,
  Pager, Scrolling,
  Item,
} from 'devextreme-react/data-grid';
import { RowUpdatingEvent } from 'devextreme/ui/data_grid';
import CustomStore from 'devextreme/data/custom_store';

import '../../../../../components/DevExtreme/style.scss'

import { useApi } from '../../../../../context/api';
import NavTabsBlock from './NavTabsBlock';
import { BlobUrlType } from './types';

const defaultPageSize = 10;
const allowedPageSizes = [10, 25, 50];


const CompanyLocationsBlobSettings = () => {
  const { apiInstance } = useApi();
  const { id } = useParams();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState<string>('');
  const [blobUrlLookupList, setBlobUrlLookupList] = useState<any>([]);

  const [blobStore, setBlobStore] = useState(new CustomStore({
      key: 'id',
      load: () => getData(`/companies/tcam/blob-url/locations-list/${id}`),
      update: (key, values) => updateData(`/companies/tcam/blob-url/assign/${key}`, key, values),
    })
  );

  useEffect(() => {
    const getCompanyName =  async () => {
      const response: any = await apiInstance.get(`/companies/edit/${id}`);
      setCompanyName(response.company_info.name);
    }

    getCompanyName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const getBlobUrlList = async (url: string) => {
    const response: any = await apiInstance.get(url);
    const list = response?.items?.map((item: any) => {
      const { id, type, blobUrl, blobRegion } = item;
      const info: string = type + '-' + blobRegion + ' — ' + blobUrl;
      return {
        id: id,
        name: info,
      }
    })
    list.unshift({
      name:'Not selected',
      id: null,
    })
    return list;
  };

  const getData = async (url: string) => {
    const response: any = await apiInstance.get(url);

    const blobUrlList: BlobUrlType [] =  await getBlobUrlList(`/companies/tcam/blob-url/list/${id}`);
    setBlobUrlLookupList(blobUrlList);

    return {
      // eslint-disable-next-line
      // @ts-ignore
      data: response?.items,
    };
  };

  const updateData = async (url: string, key: any, values: any) => {
    try {
      const res = await apiInstance.post(url, values);
      toast.success(_.get(res, 'msg', 'Saved successfully'));
      return res;
    } catch (error) {
      toast.error(
        _.get(
          error, 'error.message', 'Error while saving. Please check the parameters'
        ));
      throw new Error(JSON.stringify(error));
    }
    return {};
  }

  const handleRowUpdating = (e: RowUpdatingEvent<Partial<{id: any}>>) => {
    e.newData = {
      ...e.oldData,
      ...e.newData,
    };
  };

  return (
    <Paper
      sx={{ width: '100%' }}
      elevation={2}
      data-test="company-locations-blob-settings"
    >
      <NavTabsBlock/>
      <DataGrid
        id="gridContainer"
        data-test="grid-container"
        dataSource={blobStore}
        allowColumnReordering={true}
        rowAlternationEnabled={true}
        repaintChangesOnly={true}
        customizeColumns={(columns) => {
          columns.forEach(col => {
            col.alignment = 'left';
          });
        }}
        showBorders={true}
        onRowUpdating={handleRowUpdating}
      >
        <Editing
          mode="row"
          allowUpdating={true}
          useIcons={true}
        />
        <Scrolling mode="standard" />
        <Toolbar
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          elementAttr={{ class: 'dx-toolbar-custom' }}
        >
          <Item
            location="before"
            locateInMenu="never"
          >
            <Typography
              data-test="title-table"
              variant="h5"
              sx={{ marginRight: 2 }}
            >
              <span
                onClick={() => {
                  navigate('/companies/list');
                }}
                data-test={'back-button'}
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px"
                }}
              >
                <ChevronLeftIcon sx={{ color: 'black' }} />
              </span>
              Blob Settings {companyName}
            </Typography>
          </Item>
          <Item
            name="addRowButton"
            data-test={'add-row-button'}
            location="after"
          />
          <Item
            name="searchPanel"
            data-test={'search-panel'}
            location="after"
            cssClass={'dx-searchinput-custom'}
          />
        </Toolbar>
        <SearchPanel visible={true} highlightCaseSensitive={true} />
        <Paging
          defaultPageSize={defaultPageSize}
          enabled={true}
        />
        <Pager
          allowedPageSizes={allowedPageSizes}
          showNavigationButtons={true}
        />
        <Column
          dataField="name"
          caption="Location"
          allowEditing={false}
        />
        <Column
          dataField="serial"
          caption="MAC"
          allowEditing={false}
        />
        <Column
          dataField="blobUrlId"
          caption="Blob URL"
          minWidth={450}
          calculateDisplayValue={
            (rowData) => {
              const {
                blobUrlType = '',
                blobUrlRegion = '',
                blobUrl = '',
                id = null,
              } = rowData;
              let info: string;
              if (!blobUrl || !id) {
                info = '';
              } else {
                info = blobUrlType + '-' + blobUrlRegion + ' — ' + blobUrl;
              }
              return info;
            }
          }
        >
          <Lookup
            dataSource={{
              store: blobUrlLookupList,
              paginate: true,
              pageSize: 10,
            }}
            valueExpr="id"
            displayExpr="name"
          />
        </Column>
        <Column
          type='buttons'
          caption={'Actions'}
        />
      </DataGrid>
    </Paper>
  );
}

export default CompanyLocationsBlobSettings;
